import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Map as MapLibre,
  Marker,
  NavigationControl,
} from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { toast } from 'sonner';

import { MarkerList } from '../MarkerList';
import { MarkerInfoWindow } from '../MarkerInfoWindow';
import { MapStyleToggle } from '../MapStyleToggle';
import { PinDropDialog } from '../PinDropDialog';
import { Loader } from '../../Loader';
import { NICE_ANCHOR } from '../../../../config/geo';
import { isValidCoords } from '../../../../utils/utils';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { useMapLongPress } from '../../../../hooks/useMapLongPress';
import { MapEditContext } from '../../../../contexts/MapEditContext';

const mapKey = process.env.REACT_APP_MAPTILER_KEY;
const mapStyles = {
  streets: `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapKey}`,
  satellite: `https://api.maptiler.com/maps/hybrid/style.json?key=${mapKey}`,
};
const MAP_STYLE_STORAGE_KEY = 'mapStyle';
const MIN_PIN_ZOOM = 15;
const DROPPED_PIN_COLOR = '#e11d48';

const toLngLat = point => {
  if (!point) return null;
  const lng = point.lng ?? point.longitude;
  const lat = point.lat ?? point.latitude;
  if (!isValidCoords(lat, lng)) return null;
  return { lng: Number(lng), lat: Number(lat) };
};

function Map({
  realties = [],
  realty = false,
  highlightId = null,
  onActiveMarkerChange,
  hideBottomSheet = false,
  isLoading = false,
}) {
  const isMobile = useIsMobile();
  const mapRef = useRef(null);

  const validMarkers = useMemo(
    () =>
      (realties ?? []).filter(m => isValidCoords(m?.latitude, m?.longitude)),
    [realties]
  );

  const singlePoint = useMemo(
    () => (realty ? toLngLat(realty) : null),
    [realty]
  );

  const highlighted = useMemo(() => {
    if (highlightId == null || !validMarkers.length) return null;
    const key = String(highlightId);
    const match = validMarkers.find(r => String(r?.id) === key);
    if (!match) return null;
    return {
      id: match.id,
      lng: Number(match.longitude),
      lat: Number(match.latitude),
    };
  }, [highlightId, validMarkers]);

  const [activeMarker, setActiveMarker] = useState(null);

  useEffect(() => {
    if (highlighted?.id != null) {
      setActiveMarker(highlighted.id);
    }
  }, [highlighted?.id]);

  // null, not undefined: the initial empty selection must not wipe ?highlight= before data arrives
  const lastReportedRef = useRef(null);
  useEffect(() => {
    if (!onActiveMarkerChange) return;
    if (lastReportedRef.current === activeMarker) return;
    lastReportedRef.current = activeMarker;
    onActiveMarkerChange(activeMarker);
  }, [activeMarker, onActiveMarkerChange]);

  const fitToMarkers = useCallback(
    map => {
      if (highlighted) {
        map.flyTo({
          center: [highlighted.lng, highlighted.lat],
          zoom: 14,
          duration: 0,
        });
        return;
      }
      if (realty && singlePoint) {
        map.flyTo({
          center: [singlePoint.lng, singlePoint.lat],
          zoom: 14,
          duration: 0,
        });
        return;
      }
      if (validMarkers.length > 0) {
        let minLng = Infinity,
          minLat = Infinity,
          maxLng = -Infinity,
          maxLat = -Infinity;
        validMarkers.forEach(m => {
          const lng = Number(m.longitude);
          const lat = Number(m.latitude);
          if (lng < minLng) minLng = lng;
          if (lat < minLat) minLat = lat;
          if (lng > maxLng) maxLng = lng;
          if (lat > maxLat) maxLat = lat;
        });
        if (Number.isFinite(minLng) && Number.isFinite(minLat)) {
          map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: 48,
              maxZoom: 14,
              duration: 0,
            }
          );
        }
      }
    },
    [highlighted, realty, singlePoint, validMarkers]
  );

  const hasFittedRef = useRef(false);

  const handleLoad = useCallback(
    evt => {
      mapRef.current = evt.target;
      if (validMarkers.length || singlePoint) {
        fitToMarkers(evt.target);
        hasFittedRef.current = true;
      }
    },
    [fitToMarkers, validMarkers.length, singlePoint]
  );

  // data can land after the map has loaded (direct link / refresh)
  useEffect(() => {
    if (hasFittedRef.current || !mapRef.current || !validMarkers.length) return;
    fitToMarkers(mapRef.current);
    hasFittedRef.current = true;
  }, [fitToMarkers, validMarkers.length]);

  const handleActiveMarker = markerId => {
    setActiveMarker(prev => (prev === markerId ? null : markerId));
  };

  const [styleKey, setStyleKey] = useState(() =>
    localStorage.getItem(MAP_STYLE_STORAGE_KEY) === 'satellite'
      ? 'satellite'
      : 'streets'
  );

  const handleStyleChange = value => {
    setStyleKey(value);
    localStorage.setItem(MAP_STYLE_STORAGE_KEY, value);
  };

  const canDropPin = !realty && !hideBottomSheet;
  const [droppedPin, setDroppedPin] = useState(null);
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);

  const handlePinDrop = useCallback(
    (lngLat, originalEvent) => {
      const map = mapRef.current;

      // only presses on the map itself, not on markers / popups
      if (!canDropPin || !map || originalEvent?.target !== map.getCanvas()) {
        return;
      }

      if (map.getZoom() < MIN_PIN_ZOOM) {
        toast('Zoom in to place a pin');
        return;
      }

      setActiveMarker(null);
      setDroppedPin({ lng: lngLat.lng, lat: lngLat.lat });
      setIsPinDialogOpen(true);
    },
    [canDropPin]
  );

  const clearDroppedPin = () => {
    setIsPinDialogOpen(false);
    setDroppedPin(null);
  };

  const longPressHandlers = useMapLongPress(handlePinDrop);

  const handleContextMenu = event => {
    event.preventDefault();

    if (!isMobile) {
      handlePinDrop(event.lngLat, event.originalEvent);
    }
  };

  useEffect(() => {
    if (!droppedPin) return;

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setIsPinDialogOpen(false);
        setDroppedPin(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [droppedPin]);

  const initialViewState = highlighted
    ? { longitude: highlighted.lng, latitude: highlighted.lat, zoom: 14 }
    : singlePoint
      ? { longitude: singlePoint.lng, latitude: singlePoint.lat, zoom: 14 }
      : { longitude: NICE_ANCHOR.lng, latitude: NICE_ANCHOR.lat, zoom: 10 };

  const activeRealty = useMemo(
    () => validMarkers.find(r => r?.id === activeMarker) ?? null,
    [validMarkers, activeMarker]
  );

  return (
    <MapEditContext.Provider value={true}>
    <div className="relative w-full h-full">
      <MapLibre
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyles[styleKey]}
        onLoad={handleLoad}
        onContextMenu={handleContextMenu}
        {...(isMobile ? longPressHandlers : {})}
        attributionControl={false}
      >
        <NavigationControl
          position="top-right"
          showCompass={false}
        />

        {realty && singlePoint ? (
          <Marker
            longitude={singlePoint.lng}
            latitude={singlePoint.lat}
            anchor="bottom"
          />
        ) : (
          <MarkerList
            markers={validMarkers}
            handleActiveMarker={handleActiveMarker}
            activeMarker={activeMarker}
            setActiveMarker={setActiveMarker}
            selectedId={activeMarker}
            renderPopupInline={!isMobile}
          />
        )}

        {droppedPin && (
          <Marker
            longitude={droppedPin.lng}
            latitude={droppedPin.lat}
            anchor="bottom"
            color={DROPPED_PIN_COLOR}
            draggable
            onDragEnd={event =>
              setDroppedPin({ lng: event.lngLat.lng, lat: event.lngLat.lat })
            }
            onClick={event => {
              event.originalEvent.stopPropagation();
              setIsPinDialogOpen(true);
            }}
          />
        )}
      </MapLibre>

      {droppedPin && (
        <PinDropDialog
          key={`${droppedPin.lat},${droppedPin.lng}`}
          pin={droppedPin}
          isOpen={isPinDialogOpen}
          onClose={() => setIsPinDialogOpen(false)}
          onRemovePin={clearDroppedPin}
          onSaved={clearDroppedPin}
        />
      )}

      <MapStyleToggle
        value={styleKey}
        onChange={handleStyleChange}
      />

      {isLoading && (
        <div className="absolute inset-0 z-[3] flex items-center justify-center bg-white/60">
          <Loader
            width={56}
            height={56}
          />
        </div>
      )}

      {isMobile && activeRealty && !hideBottomSheet && (
        <MarkerInfoWindow
          realty={activeRealty}
          setActiveMarker={setActiveMarker}
        />
      )}
    </div>
    </MapEditContext.Provider>
  );
}

export default React.memo(Map);
